import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Loader2, AlertCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '~/components/ui/card';
import { useGetInquiryFormInfo } from './useGetInquiryFormInfo';
import { useCreateAdmissionInquiry } from './useCreateAdmissionInquiry';

function InquiryForm() {
  const { token } = useParams();
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const [getFormInfo, { loading: loadingInfo }] = useGetInquiryFormInfo();
  const [createInquiry] = useCreateAdmissionInquiry();

  const [schoolData, setSchoolData] = useState<Record<string, unknown> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [iframeReady, setIframeReady] = useState(false);

  // Fetch school data on mount
  useEffect(() => {
    if (!token) return;
    getFormInfo({ variables: { token } }).then((result) => {
      const data = result.data?.getInquiryFormInfo;
      if (data?.userErrors && data.userErrors.length > 0) {
        setError(data.userErrors[0].message);
        return;
      }
      if (data) {
        setSchoolData({
          schoolName: data.schoolName,
          schoolYearName: data.schoolYearName,
        });
      }
    }).catch((err) => {
      setError(err.message);
    });
  }, [token, getFormInfo]);

  // Send school data to iframe when both are ready
  useEffect(() => {
    if (iframeReady && schoolData && iframeRef.current?.contentWindow) {
      iframeRef.current.contentWindow.postMessage({
        type: 'inquiry_init',
        ...schoolData,
      }, '*');
    }
  }, [iframeReady, schoolData]);

  // Handle messages from iframe
  const handleMessage = useCallback(async (event: MessageEvent) => {
    if (!event.data?.type) return;

    if (event.data.type === 'inquiry_ready') {
      setIframeReady(true);
      return;
    }

    if (event.data.type === 'inquiry_submission') {
      try {
        const result = await createInquiry({
          variables: {
            input: {
              token: token!,
              firstName: event.data.firstName,
              lastName: event.data.lastName,
              email: event.data.email,
              phone: event.data.phone,
              numStudents: event.data.numStudents,
              formData: event.data.formData,
            },
          },
        });

        const data = result.data?.createAdmissionInquiry;
        if (data?.userErrors && data.userErrors.length > 0) {
          // Tell iframe about the error so it can show it
          iframeRef.current?.contentWindow?.postMessage({
            type: 'inquiry_error',
            message: data.userErrors[0].message,
          }, '*');
        }
        // On success, iframe already shows confirmation
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Something went wrong';
        iframeRef.current?.contentWindow?.postMessage({
          type: 'inquiry_error',
          message,
        }, '*');
      }
    }
  }, [token, createInquiry]);

  useEffect(() => {
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [handleMessage]);

  // Error state (before iframe loads)
  if (error && !schoolData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-destructive">
              <AlertCircle className="h-5 w-5" />
              Unable to Load Form
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Loading state
  if (loadingInfo) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-primary" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <iframe
      ref={iframeRef}
      src="/forms/inquiry-immanuel.html"
      style={{
        width: '100%',
        height: '100vh',
        border: 'none',
        display: 'block',
      }}
      title="Inquiry Form"
    />
  );
}

export default InquiryForm;
